import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { buildIframeDocument, resolveTemplateUrl } from '../lib/templateEngine'
import { privateShareTitle, publicShareTitle } from '../lib/shareMeta'
import { IconHeart, IconSparkles, IconAlertCircle, IconRefreshCw, IconChevronLeft, IconEdit3 } from './Icons'
import '../pages/dashboard/simple-page-responsive.css'

const TemplateLoader = () => {
  const { slug } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState(null)
  const [templateContent, setTemplateContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [iframeKey, setIframeKey] = useState(0)

  let currentUser = null
  try {
    const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null
    if (userStr) currentUser = JSON.parse(userStr)
  } catch {}
  const isOwner = currentUser && invitation && (currentUser.id === invitation.users_id || currentUser.role === 'admin')

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true)
        setError(null)

        // Try to get invitation by slug
        let invData = null
        try {
          const response = await api.getInvitationBySlug(slug)
          invData = response.data?.data || response.data
        } catch (err) {
          // If slug endpoint doesn't exist, try to get all and filter
          try {
            const response = await api.getInvitations()
            const invsData = Array.isArray(response.data?.data)
              ? response.data.data
              : Array.isArray(response.data)
              ? response.data
              : []
            invData = invsData.find((inv) => inv.invitation_slug === slug)
          } catch (fallbackErr) {
            throw err // Throw original error if fallback also fails
          }
        }

        if (!invData) {
          throw new Error(`Không tìm thấy thiệp cưới với đường dẫn: ${slug}`)
        }

        // Đọc thông tin khách mời từ link riêng (link ngắn ?to=..., ?guest=..., mã ?g=..., hoặc token cũ ?guest_name=...)
        const currentSearch = (typeof window !== 'undefined' ? window.location.search : '') || location.search || ''
        const searchParams = new URLSearchParams(currentSearch)
        const guestNameParam = searchParams.get('to') || searchParams.get('guest') || searchParams.get('guest_name') || searchParams.get('gname') || ''
        const guestCodeParam = searchParams.get('g') || searchParams.get('gid') || searchParams.get('guest_id') || ''
        const customTimeParam = searchParams.get('time') || searchParams.get('t') || searchParams.get('reception_time') || ''

        let resolvedGuestName = ''
        let resolvedGuestId = guestCodeParam || ''
        let resolvedGuestNote = ''
        let guestDataFetched = null

        // 1. Nếu có mã ngắn khách mời (?g=...) - ưu tiên hàng đầu cho link rút gọn hiện đại
        if (guestCodeParam) {
          try {
            const guestRes = await api.getPublicGuest(guestCodeParam)
            const gData = guestRes.data?.data || guestRes.data
            if (gData && gData.name) {
              resolvedGuestName = gData.name
              resolvedGuestId = gData.id || resolvedGuestId
              if (gData.description) resolvedGuestNote = gData.description
              guestDataFetched = gData
            }
          } catch (_e) {}
        }

        // 2. Nếu có tên truyền qua query (?to=...)
        if (!resolvedGuestName && guestNameParam) {
          let tokenSuccess = false
          // Chuỗi token mã hoá từ BE PrivateInvitation cũ có độ dài >= 30
          if (guestNameParam.length >= 30) {
            try {
              const guestRes = await api.getPublicGuest(guestNameParam)
              const gData = guestRes.data?.data || guestRes.data
              if (gData && gData.name) {
                resolvedGuestName = gData.name
                resolvedGuestId = gData.id || resolvedGuestId
                resolvedGuestNote = gData.description || ''
                guestDataFetched = gData
                tokenSuccess = true
              }
            } catch (_e) {
              // Token không hợp lệ hoặc lỗi mạng, fallback sang đọc tên thường
            }
          }
          if (!tokenSuccess) {
            try {
              resolvedGuestName = decodeURIComponent(guestNameParam.replace(/\+/g, ' ')).trim()
            } catch (_e) {
              resolvedGuestName = guestNameParam.replace(/\+/g, ' ').trim()
            }
          }
        }

        let resolvedReceptionTime = invData.reception_time || (invData.extra_data && invData.extra_data.reception_time) || ''
        if (guestDataFetched?.reception_time) {
          resolvedReceptionTime = guestDataFetched.reception_time
        } else if (customTimeParam) {
          try {
            resolvedReceptionTime = decodeURIComponent(customTimeParam.replace(/\+/g, ' ')).trim()
          } catch (_e) {
            resolvedReceptionTime = customTimeParam.replace(/\+/g, ' ').trim()
          }
        }

        const enrichedInvitation = {
          ...invData,
          guest_name: resolvedGuestName || invData.guest_name || '',
          guest_id: resolvedGuestId || invData.guest_id || '',
          guest_note: resolvedGuestNote || invData.guest_note || '',
          reception_time: resolvedReceptionTime || invData.reception_time || '11:00',
        }

        // Hữu ích khi mở trực tiếp trong trình duyệt; các bot Zalo/Messenger nhận
        // cùng tiêu đề từ thẻ Open Graph được FE server sinh trước khi React tải.
        if (typeof document !== 'undefined') {
          document.title = resolvedGuestName
            ? privateShareTitle(invData, resolvedGuestName)
            : publicShareTitle(invData)
        }

        setInvitation(enrichedInvitation)

        // Mẫu thiệp được xác định qua invData.template (join sẵn từ BE)
        const templateUrl = resolveTemplateUrl(invData.template?.html_path)
        if (!templateUrl) {
          throw new Error('Thiệp này chưa được gán mẫu giao diện')
        }

        const htmlContent = await loadTemplateHTML(templateUrl)
        setTemplateContent(htmlContent)
      } catch (err) {
        console.error('Error loading invitation:', err)
        setError(err.message || 'Không thể tải thiệp cưới')
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      loadInvitation()
    }
  }, [slug, location.search])

  // Điều hướng nội bộ và mở liên kết ngoài (Google Maps, Chỉ đường) cho Zalo / Messenger
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return

      // Mở liên kết chỉ đường Google Maps từ iframe ra ngoài app bản đồ (khắc phục lỗi đứng yên trên Zalo/Messenger WebView)
      if (event.data.type === 'wedding-web:open-map' || event.data.type === 'wedding-web:open-external-url') {
        const url = event.data.url
        if (url) {
          try {
            window.location.href = url
          } catch (_e) {
            window.open(url, '_blank')
          }
        }
        return
      }

      if (event.data.type !== 'wedding-web:navigate' || !event.data.file) return

      const baseUrl = resolveTemplateUrl(invitation?.template?.html_path)
      if (!baseUrl) return

      try {
        const fullBase = /^https?:\/\//i.test(baseUrl)
          ? baseUrl
          : (typeof window !== 'undefined' ? new URL(baseUrl, window.location.origin).href : baseUrl)
        const nextUrl = new URL(event.data.file, fullBase).href
        loadTemplateHTML(nextUrl)
          .then((html) => {
            setTemplateContent(html)
            setIframeKey((prev) => prev + 1)
          })
          .catch((err) => console.error('Error navigating within template:', err))
      } catch (err) {
        console.error('Error navigating within template:', err)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [invitation])

  const loadTemplateHTML = async (templateUrl) => {
    const bustUrl = `${templateUrl}${templateUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
    const response = await fetch(bustUrl, { cache: 'no-store' })
    if (!response.ok) {
      throw new Error(`Failed to load template: ${templateUrl}`)
    }
    return response.text()
  }

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingCard}>
          <div style={styles.iconHeartWrap}>
            <IconHeart size={28} color="#fff" />
          </div>
          <h2 style={styles.loadingTitle}>Đang Mở Thiệp Cưới...</h2>
          <p style={styles.loadingSub}>Vui lòng chờ trong giây lát</p>
          <div style={styles.spinner}></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorContent}>
          <div style={styles.errorIconWrap}>
            <IconAlertCircle size={32} color="#c04938" />
          </div>
          <h2 style={styles.errorTitle}>Không Thể Mở Thiệp Cưới</h2>
          <p style={styles.errorMessage}>{error}</p>
          <div style={styles.errorHint}>
            <p style={{ fontWeight: 600, color: '#1f1917', marginBottom: 6 }}>💡 Gợi ý xử lý:</p>
            <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.8 }}>
              <li>Kiểm tra lại đường dẫn (slug) đã nhập chính xác chưa</li>
              <li>Kiểm tra kết nối mạng hoặc thử làm mới lại trang</li>
              <li>Liên hệ với cô dâu hoặc chú rể để nhận lại link thiệp mời</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => window.location.reload()} style={styles.reloadBtn}>
              <IconRefreshCw size={15} />
              <span>Tải Lại Trang</span>
            </button>
            <button onClick={() => window.history.back()} style={styles.backBtn}>
              <IconChevronLeft size={15} />
              <span>Quay Lại</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {isOwner && (
        <div className="public-owner-toolbar" style={styles.ownerToolbar}>
          <button
            onClick={() => navigate(`/editor/${invitation.id}`)}
            className="public-owner-edit"
            style={styles.ownerEditBtn}
            title="Mở trình chỉnh sửa nội dung và ảnh trên thiệp này"
          >
            <IconEdit3 size={16} />
            <span>Chỉnh Sửa Thiệp</span>
          </button>
        </div>
      )}
      <iframe
        key={iframeKey}
        srcDoc={buildIframeDocument(templateContent, invitation)}
        className="public-invitation-frame"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          margin: 0,
          padding: 0,
          display: 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-forms allow-modals"
        title="Wedding Invitation"
      />
    </>
  )
}

const styles = {
  ownerToolbar: {
    position: 'fixed',
    top: 14,
    right: 14,
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  ownerEditBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13.5,
    fontWeight: 600,
    boxShadow: '0 6px 20px rgba(109, 40, 217, 0.4)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  loadingContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'radial-gradient(ellipse at center, #fdf7f2 0%, #f4e8dc 100%)',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    padding: 20,
  },
  loadingCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    border: '1px solid #ede5db',
    borderRadius: 24,
    padding: '40px 36px',
    textAlign: 'center',
    boxShadow: '0 16px 40px -8px rgba(31, 25, 23, 0.1)',
    maxWidth: 380,
    width: '100%',
  },
  iconHeartWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e58d6f 0%, #c96547 100%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    boxShadow: '0 4px 16px rgba(217, 119, 87, 0.35)',
  },
  loadingTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    color: '#1f1917',
    marginBottom: 6,
  },
  loadingSub: {
    fontSize: 13.5,
    color: '#6e625c',
    marginBottom: 20,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(217, 119, 87, 0.15)',
    borderTopColor: '#d97757',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto',
  },
  errorContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#fcfaf7',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    padding: 20,
  },
  errorContent: {
    textAlign: 'center',
    maxWidth: 480,
    width: '100%',
    padding: '36px 32px',
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 16px 40px -8px rgba(31, 25, 23, 0.1)',
    border: '1px solid #ede5db',
  },
  errorIconWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'rgba(192, 73, 56, 0.1)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  errorTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#1f1917',
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 14,
    marginBottom: 18,
    color: '#c04938',
    fontWeight: 500,
  },
  errorHint: {
    fontSize: 13,
    lineHeight: 1.7,
    color: '#5c524c',
    textAlign: 'left',
    background: '#f6f1eb',
    padding: '16px 18px',
    borderRadius: 14,
    marginBottom: 24,
    border: '1px solid #ede5db',
  },
  reloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '10px 22px',
    background: 'linear-gradient(135deg, #e58d6f 0%, #c96547 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13.5,
    fontWeight: 600,
    boxShadow: '0 4px 14px rgba(217, 119, 87, 0.35)',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 20px',
    background: '#f6f1eb',
    color: '#1f1917',
    border: '1px solid #ede5db',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 13.5,
    fontWeight: 500,
  },
}

export default TemplateLoader
